import { z } from "zod/v4";

/**
 * The durable contract for one provisional mutation of a Bridged Stripe
 * Subscription.
 *
 * The billing workflow persists and routes this envelope but does not interpret
 * Stripe state: `providerPayload` is opaque and belongs to StripeApi alone. The
 * outer envelope is versioned separately from that payload so Stripe
 * restoration data can evolve without forcing every workflow reader to change,
 * and so an outer shape a reader does not recognize fails closed instead of
 * being silently narrowed.
 */

export const BridgeMutationRetentionList = [
  "retain_existing",
  "remove_command_created",
] as const;

export type BridgeMutationRetention =
  (typeof BridgeMutationRetentionList)[number];

/**
 * Why the bridge reached its current association. Provenance only — it never
 * decides whether compensation may tear a bridge down. That is `retention`.
 */
export const BridgeMutationTransitionList = [
  "created",
  "adopted",
  "converted",
  "replaced",
] as const;

export type BridgeMutationTransition =
  (typeof BridgeMutationTransitionList)[number];

export const BridgeMutationLifecycleV1Schema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("prepared"), preparedAt: z.string().min(1) }),
  z.object({
    state: z.literal("pending_approval"),
    appliedAt: z.string().min(1),
  }),
  z.object({
    state: z.literal("approval_observed"),
    observedAt: z.string().min(1),
  }),
  z.object({
    state: z.literal("rejection_observed"),
    observedAt: z.string().min(1),
  }),
  z.object({
    state: z.literal("provider_committed"),
    completedAt: z.string().min(1),
  }),
  z.object({
    state: z.literal("rolled_back"),
    completedAt: z.string().min(1),
  }),
]);

export type BridgeMutationLifecycleV1 = z.infer<
  typeof BridgeMutationLifecycleV1Schema
>;

export type BridgeMutationLifecycleState = BridgeMutationLifecycleV1["state"];

export const BridgeMutationProviderPayloadSchema = z.object({
  schemaVersion: z.string().min(1),
  data: z.unknown(),
});

export type BridgeMutationProviderPayload = z.infer<
  typeof BridgeMutationProviderPayloadSchema
>;

export const BridgeMutationReceiptV1Schema = z
  .object({
    schemaVersion: z.literal("v1"),
    receiptId: z.string().min(1),
    ownerCommandId: z.string().min(1),
    organizationId: z.string().min(1),
    stripeSubscriptionId: z.string().min(1),
    retention: z.enum(BridgeMutationRetentionList),
    transition: z.enum(BridgeMutationTransitionList),
    holdToken: z.string().min(1),
    lifecycle: BridgeMutationLifecycleV1Schema,
    providerPayload: BridgeMutationProviderPayloadSchema,
  })
  .strict();

export type BridgeMutationReceiptV1 = z.infer<
  typeof BridgeMutationReceiptV1Schema
>;

export const BridgeMutationRecoveryRefV1Schema = z.object({
  ownerCommandId: z.string().min(1),
  organizationId: z.string().min(1),
  expectedReceiptId: z.string().min(1),
});

export type BridgeMutationRecoveryRefV1 = z.infer<
  typeof BridgeMutationRecoveryRefV1Schema
>;

export const BridgeMutationReceiptResolutionV1Schema = z.object({
  receiptId: z.string().min(1),
  resolvedAt: z.string().min(1),
});

export type BridgeMutationReceiptResolutionV1 = z.infer<
  typeof BridgeMutationReceiptResolutionV1Schema
>;

const RECOVERY_REF_KEY = "bridgeMutationRecoveryRef";
const RECEIPT_RESOLUTION_KEY = "bridgeMutationReceiptResolution";

/**
 * The recovery ref a Command still owes operator action on, read off the error
 * context a settling Command leaves behind.
 *
 * A ref alone does not mean the receipt is still parked: convergence is
 * idempotent and can be driven from elsewhere, so the operator surface would
 * keep offering a resolved receipt forever. The resolution stamp is what
 * retires the ref — and until it lands the owning Command is unsafe to replay,
 * because the hold the receipt holds is still published.
 */
export function readUnresolvedBridgeMutationRecoveryRef(
  errorContextJson: unknown
): BridgeMutationRecoveryRefV1 | null {
  const context = asContextRecord(errorContextJson);
  if (!context) {
    return null;
  }

  const ref = BridgeMutationRecoveryRefV1Schema.safeParse(
    context[RECOVERY_REF_KEY]
  );
  if (!ref.success) {
    return null;
  }

  const resolution = BridgeMutationReceiptResolutionV1Schema.safeParse(
    context[RECEIPT_RESOLUTION_KEY]
  );
  if (
    resolution.success &&
    resolution.data.receiptId === ref.data.expectedReceiptId
  ) {
    return null;
  }

  return ref.data;
}

/**
 * Retires a recovery ref by stamping the resolution beside it. The ref itself
 * is preserved: it names what was resolved, and an operator reading the command
 * later needs both halves.
 */
export function stampBridgeMutationReceiptResolution(
  errorContextJson: unknown,
  resolution: BridgeMutationReceiptResolutionV1
): Record<string, unknown> {
  return {
    ...(asContextRecord(errorContextJson) ?? {}),
    [RECEIPT_RESOLUTION_KEY]: resolution,
  };
}

function asContextRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

const TERMINAL_LIFECYCLE_STATES: readonly BridgeMutationLifecycleState[] = [
  "provider_committed",
  "rolled_back",
];

/**
 * The only lifecycle moves that exist. Approval and rejection are separate
 * durable boundaries that each admit exactly one convergence direction, so an
 * observed approval can never reach rollback and an observed rejection can
 * never reach commit — no matter how many times convergence is retried.
 *
 * `prepared` → `rolled_back` is abandonment: nothing was observed and, by
 * prepare's contract, nothing was written to Stripe, so resolving straight to
 * the rolled-back terminal contradicts no durable observation.
 */
const LIFECYCLE_SUCCESSORS: Record<
  BridgeMutationLifecycleState,
  readonly BridgeMutationLifecycleState[]
> = {
  prepared: ["pending_approval", "rolled_back"],
  pending_approval: ["approval_observed", "rejection_observed"],
  approval_observed: ["provider_committed"],
  rejection_observed: ["rolled_back"],
  provider_committed: [],
  rolled_back: [],
};

export function isTerminalBridgeMutationLifecycle(
  lifecycle: BridgeMutationLifecycleV1
): boolean {
  return TERMINAL_LIFECYCLE_STATES.includes(lifecycle.state);
}

export type AdvanceBridgeMutationLifecycleResult =
  | { outcome: "advanced"; lifecycle: BridgeMutationLifecycleV1 }
  | { outcome: "already_recorded"; lifecycle: BridgeMutationLifecycleV1 }
  | { outcome: "forbidden"; reason: string };

/**
 * Applies one lifecycle move, or refuses it.
 *
 * Convergence is retried until StripeApi read-verifies it, so re-recording the
 * state a receipt already holds is normal and reports the durable value
 * unchanged rather than restamping its timestamp — the first observation is the
 * one that happened.
 */
export function advanceBridgeMutationLifecycle(
  current: BridgeMutationLifecycleV1,
  next: BridgeMutationLifecycleV1
): AdvanceBridgeMutationLifecycleResult {
  if (current.state === next.state) {
    return { outcome: "already_recorded", lifecycle: current };
  }

  if (!LIFECYCLE_SUCCESSORS[current.state].includes(next.state)) {
    return {
      outcome: "forbidden",
      reason: `bridge mutation lifecycle cannot move from ${current.state} to ${next.state}`,
    };
  }

  return { outcome: "advanced", lifecycle: next };
}

export type ParseBridgeMutationReceiptResult =
  | { outcome: "absent" }
  | { outcome: "receipt"; receipt: BridgeMutationReceiptV1 }
  | { outcome: "unsupported"; schemaVersion: string | null };

/**
 * Reads a persisted receipt without ever widening what the reader understands.
 *
 * Anything present but unrecognized — a newer outer version, a shape this
 * reader cannot validate — resolves to `unsupported` rather than `absent`, so a
 * reader deployed before its writer preserves the hold and refuses takeover
 * instead of treating live provisional state as nothing at all.
 */
export function parseBridgeMutationReceipt(
  value: unknown
): ParseBridgeMutationReceiptResult {
  if (value === undefined || value === null) {
    return { outcome: "absent" };
  }

  const declaredVersion =
    typeof value === "object" && !Array.isArray(value)
      ? (value as { schemaVersion?: unknown }).schemaVersion
      : undefined;
  const schemaVersion =
    typeof declaredVersion === "string" ? declaredVersion : null;

  const parsed = BridgeMutationReceiptV1Schema.safeParse(value);
  if (!parsed.success) {
    return { outcome: "unsupported", schemaVersion };
  }

  return { outcome: "receipt", receipt: parsed.data };
}

/**
 * Whether another Command must be refused the bridge. Only a receipt this
 * reader fully understands *and* has read as terminal releases it.
 */
export function bridgeMutationReadBlocksTakeover(
  parsed: ParseBridgeMutationReceiptResult
): boolean {
  switch (parsed.outcome) {
    case "absent":
      return false;
    case "receipt":
      return !isTerminalBridgeMutationLifecycle(parsed.receipt.lifecycle);
    case "unsupported":
      return true;
  }
}

/**
 * Why StripeApi refused a bridge mutation. Every code here is non-retryable:
 * nothing about waiting and trying again changes the answer, so the owning
 * Command must fail with an operator-actionable reason rather than burn retries.
 */
export const BridgeMutationRefusalCodeList = [
  // The pre-mutation snapshot could not be captured completely, so rollback
  // could not restore equivalent state. Refused before any provider write.
  "bridge_snapshot_incomplete",
  // A Stripe subscription item id has a durable consumer (a Module Trial grant),
  // and rollback does not recreate item ids.
  "bridge_item_identity_consumer",
  // A booked future schedule phase would be destroyed by releasing the schedule.
  // An operator resolves or migrates the phase, then replays the Command.
  "bridge_schedule_phase_booked",
  // The bridge is not in a state that can be item-mutated in place.
  "bridge_subscription_not_live",
  // The requested lifecycle move contradicts a durable observation.
  "bridge_lifecycle_conflict",
  // The receipt's provider payload version is newer than this StripeApi. The
  // hold stays published and the receipt stays unresolved.
  "bridge_provider_payload_unsupported",
  // The published hold belongs to a different Command's receipt.
  "bridge_hold_not_owned",
  // Converting or replacing a legacy bridge was asked for by a Command that
  // carries no conversion authority. Organization Regime alone is never
  // authorization, so this refuses instead of falling through to a create.
  "bridge_conversion_unauthorized",
  // Conversion was asked for onto something other than the modular catalog.
  // Conversion authority moves a legacy bridge to the modular catalog; it is
  // not a licence to re-point a legacy link at another legacy version.
  "bridge_conversion_target_not_modular",
  // A conversion mechanic was selected for a target that is not the modular
  // catalog. Conversion intent authorizes moving a legacy bridge onto the
  // modular catalog, never re-pointing it at another legacy catalog version.
  "bridge_conversion_target_not_modular",
] as const;

export type BridgeMutationRefusalCode =
  (typeof BridgeMutationRefusalCodeList)[number];

/**
 * The Shopify charge hold, as published on the Bridged Stripe Subscription's own
 * metadata.
 *
 * The charge cron reads Stripe, not workflow storage, so the prohibition has to
 * live where it can already see it. It is deliberately coarse: any non-empty
 * value blocks posting, so charging never has to understand a receipt version to
 * stay safe. Clearing is the narrow operation — only the receipt whose
 * `holdToken` matches may release it, so one Command cannot free another's
 * quarantine.
 */
export function readShopifyChargeHold(metadata: {
  shopifyChargeHold?: string | null;
}): string | null {
  const hold = metadata.shopifyChargeHold?.trim();
  return hold ? hold : null;
}

export function shopifyChargeHoldIsOwnedBy(
  metadata: { shopifyChargeHold?: string | null },
  receipt: Pick<BridgeMutationReceiptV1, "holdToken">
): boolean {
  return readShopifyChargeHold(metadata) === receipt.holdToken;
}

export function buildBridgeMutationHoldToken(options: {
  ownerCommandId: string;
  receiptId: string;
}): string {
  return `${options.ownerCommandId}:${options.receiptId}`;
}

export type BridgeRetentionDecision = {
  retention: BridgeMutationRetention | "undetermined";
  source:
    | "receipt"
    | "metadata"
    | "legacy_bridge_origin"
    | "unsupported_receipt"
    | "absent";
};

type BridgeRetentionMetadata = {
  bridgeMutationReceipt?: unknown;
  bridgeRetention?: unknown;
  bridgeOrigin?: unknown;
};

/**
 * The retention decision for one correlation row, across every generation of
 * bridge metadata.
 *
 * A receipt states retention outright. Rows written before receipts carry only
 * `bridgeOrigin`, whose historical `"adopted"` value is exactly the retain
 * rule, so it keeps meaning that. A row with neither stays `undetermined`:
 * callers that already have a conservative fallback for unrecorded provenance
 * must keep using it rather than have this reader invent a destructive answer.
 */
export function readBridgeRetentionDecision(
  metadata: BridgeRetentionMetadata
): BridgeRetentionDecision {
  const parsedReceipt = parseBridgeMutationReceipt(
    metadata.bridgeMutationReceipt
  );
  if (parsedReceipt.outcome === "receipt") {
    return { retention: parsedReceipt.receipt.retention, source: "receipt" };
  }
  if (parsedReceipt.outcome === "unsupported") {
    return { retention: "retain_existing", source: "unsupported_receipt" };
  }

  const explicit = z
    .enum(BridgeMutationRetentionList)
    .safeParse(metadata.bridgeRetention);
  if (explicit.success) {
    return { retention: explicit.data, source: "metadata" };
  }

  if (metadata.bridgeOrigin === "adopted") {
    return { retention: "retain_existing", source: "legacy_bridge_origin" };
  }
  if (metadata.bridgeOrigin === "created") {
    return {
      retention: "remove_command_created",
      source: "legacy_bridge_origin",
    };
  }

  return { retention: "undetermined", source: "absent" };
}
